import * as util from "./util.js";
import * as cconst from "./const.js";
import * as sinterface from "./interface.js";
import type { AppState } from "./app.js";
import type { UI } from "./ui.js";
import * as vectormath from "./vectormath.js";
import * as kdtree from "./kdtree.js";
import * as darts from "./darts.js";
import * as fft from "./fft.js";
import report from "./report.js";
import * as presets from "./presets.js";
import * as void_cluster from "./void_cluster.js";
import type { MaskConfig } from "./interface.js";
import type { KDTree } from "./kdtree.js";
const xy2grid_rets = new util.cachering(function () {
  return [0, 0];
}, 32);
const grid2xy_rets = new util.cachering(function () {
  return [0, 0];
}, 32);
const sqrt3 = Math.sqrt(3);
const sqrt2 = Math.sqrt(2);
const hexfac = 1.0 / sqrt3;
const grid_funcs = {
  hex: {
    xy2grid: function (x: number, y: number, size: number): number[] {
      const ret = xy2grid_rets.next();

      ret[0] = x * size + 0.00001;
      ret[1] = y * size + 0.00001;

      if (~~ret[0] & 1) {
        ret[1] -= 0.5;
      }

      ret[0] -= 0.5;
      ret[1] -= 0.5;

      return ret;
    },

    grid2xy: function (ix: number, iy: number, size: number): number[] {
      if (~~ix & 1) {
        iy += 0.5;
      }

      //ix *= hexfac;

      const ret = grid2xy_rets.next();
      ret[0] = (ix + 0.5) / size;
      ret[1] = (iy + 0.5) / size;

      return ret;
    },
  },
  cartessian: {
    xy2grid: function (x: number, y: number, size: number): number[] {
      const ret = xy2grid_rets.next();

      ret[0] = x * size + 0.50001;
      ret[1] = y * size + 0.50001;

      return ret;
    },

    grid2xy: function (ix: number, iy: number, size: number): number[] {
      const ret = grid2xy_rets.next();

      ret[0] = ix / size;
      ret[1] = iy / size;

      return ret;
    },
  },
};
const Vector2 = vectormath.Vector2;
const Class = util.Class;
const MaskGenerator = sinterface.MaskGenerator;
const Modes = {
  SHUFFLE: 0,
  CONVERGE: 1,
};
const config = Object.assign(
  {
    MAKE_JOINT: false,
  },
  void_cluster.config
);
const GIDX = 0;
const GW = 1;
const GSUM = 2;
const GTAG = 3;
const GTOT = 4;
/*reuses void-cluster defaults
  let config = {
    //VOIDCLUSTER_MID_R : 0.8,
    VC_FILTERWID : 0.55,
    VC_HIEARCHIAL_SCALE : 7.6,
    TEST_CLUSTER : false,
    VOID_HEX_MODE : false,
    VOID_BAYER_MODE : false,
    VOIDCLUSTER_CURVE : new cconst.EditableCurve("VC Filter Curve", {"points":[{"0":0,"1":0,"eid":40,"flag":0,"deg":3,"tangent":1},{"0":0.07499999999999968,"1":0.03749999999999987,"eid":156,"flag":0,"deg":3,"tangent":1},{"0":0.1874999999999999,"1":0.04375000000000018,"eid":166,"flag":0,"deg":3,"tangent":1},{"0":0.48749999999999993,"1":0.03125,"eid":165,"flag":1,"deg":3,"tangent":1},{"0":0.58125,"1":0.16874999999999996,"eid":162,"flag":0,"deg":3,"tangent":1},{"0":0.64375,"1":0.70625,"eid":168,"flag":0,"deg":3,"tangent":1},{"0":0.8250000000000002,"1":0.9812500000000002,"eid":170,"flag":0,"deg":3,"tangent":1},{"0":0.9125,"1":1,"eid":169,"flag":0,"deg":3,"tangent":1},{"0":0.91875,"1":1,"eid":167,"flag":0,"deg":3,"tangent":1},{"0":0.9999999999999999,"1":0.9875,"eid":130,"flag":0,"deg":3,"tangent":1},{"0":1,"1":1,"eid":123,"flag":0,"deg":3,"tangent":1},{"0":1,"1":1,"eid":2,"flag":0,"deg":3,"tangent":1}],"eidgen":{"_cur":171}})
  };*/

const VoidClusterGeneratorCMYK = class VoidClusterGeneratorCMYK extends MaskGenerator {
  ignore_initial_points: boolean;
  cur_cmyk: number;
  hscale: number;
  gen: number;
  maxgen: number;
  totfilled: number;
  maxpoints: number;
  colortick: number;
  grids: Float64Array[];
  grid: Float64Array; // legacy singular alias; never assigned (matches original)
  midr: number; // legacy field; never assigned (matches original)
  gridsize: number;
  _color: number[];
  cells: number[][];
  midsize: number;
  midpoints: number;
  start_r: number;
  min_r: number;
  max_r: number;
  search_r: number;
  searchr: number;
  xy2grid: (x: number, y: number, size: number) => number[];
  grid2xy: (ix: number, iy: number, size: number) => number[];

  constructor(appstate?: AppState, dilute_small_mask?: boolean) {
    super(appstate, dilute_small_mask);

    this.ignore_initial_points = false;
    this.cur_cmyk = 0;

    this.hscale = 0;
    this.hlvl = 0;
    this.gen = 0;

    // Set to real values in reset(); kept as undefined here to match legacy init.
    this.grids = undefined as unknown as Float64Array[];
    this.gridsize = undefined as unknown as number;

    this._color = undefined as unknown as number[];
    this.points = undefined as unknown as number[];
  }

  static build_ui(gui: any) {
    gui.check("MAKE_JOINT", "Joint Mask", "Encoded masks into r,g,b,a channels of one image");

    //uses normal void-cluster's settings panel
    /*
      let panel2 = gui.panel("Void-Cluster");
      
      let panel3 = panel2.panel("Filter Curve");
      window.VOIDCLUSTER_CURVE = panel3.curve('VOIDCLUSTER_CURVE', 'VC Filter Curve',  cconst.DefaultCurves.VOIDCLUSTER_CURVE).curve;
      panel3.close();
      
      //panel2.slider('VOIDCLUSTER_MID_R', 'Middle Radius', 0.5, 0, 1, 0.001, false, false);
      panel2.slider('VC_FILTERWID', 'Filter Width', 0.5, 0, 16, 0.001, false, false);
      panel2.slider('VC_HIEARCHIAL_SCALE', 'Density Range', 1.0, 0, 40, 0.001, false, false);
      
      panel2.check('TEST_CLUSTER', 'Test Cluster');
      panel2.check('VOID_HEX_MODE', 'Hexagon Mode');
      panel2.check("VOID_BAYER_MODE", "Bayer Mode");
      panel2.close();
      //*/
  }

  max_level() {
    return this.maxgen;
  }

  next_level() {}

  get_visible_points(restrict: number, for_fft?: boolean) {
    const size = this.gridsize;
    let ps2 = super.get_visible_points(restrict, for_fft);

    if (for_fft && ps2.length / PTOT > size * size * 0.5) {
      ps2 = super.get_visible_points(restrict, for_fft, true);
    }
    return ps2;
  }

  gen_initial_blue(cf: MaskConfig, dimen: number, appstate?: AppState, mask_image?: ImageData) {
    const midsize = this.midsize;
    const midr = this.start_r;
    const ps = this.points;

    util.seed(0);

    for (let i = 0; i < midsize * midsize; i++) {
      const x = util.random();
      const y = util.random();
      const pi = ps.length;
      for (let j = 0; j < PTOT; j++) {
        ps.push(0);
      }

      const color = i % 4;

      (ps[pi] = x), (ps[pi + 1] = y);
      ps[pi + PR] = ps[pi + PR2] = midr / 2;
      ps[pi + PCLR] = color;

      ps[pi + PGEN] = this.maxgen++;
      this.gen++;
    }

    this.searchr = this.filter_r(cf);

    for (let i = 0; i < 10; i++) {
      this.relax();
    }

    /*
      let sgen = new sph.SPHGenerator(appstate);
      let smidsize = ~~(1.0 / (midr/Math.sqrt(2.0)));
      sgen.reset(midsize, appstate, mask_image);
      
      sgen.r = midr;
      sgen.points = ps;
      sgen.regen_spatial();
      
      for (let  i=0; i<13; i++) {
        sgen.step();
      }
      //*/
  }

  gen_initial_structured(dimen: number, appstate?: AppState, mask_image?: ImageData) {
    const ps = this.points;
    const repeat = ~~(dimen / 8);
    const midr = this.midr;
    const midsize = this.midsize;
    const totpoint = midsize * midsize * 4;
    this.maxgen = 0;
    ps.length = 0;

    const middimen = Math.max(Math.ceil(Math.sqrt(totpoint)), 1);

    //for (let ci=0; ci<4; ci++) {
    for (let i = 0; i < totpoint; i++) {
      const pi = ps.length;
      ps.length += PTOT;

      for (let j = 0; j < PTOT; j++) {
        ps[pi + j] = 0.0;
      }

      let x = i % middimen;
      let y = ~~(i / middimen);

      x += 0.5;
      y += 0.5;
      x = x / middimen;
      y = y / middimen;

      x += ((util.random() - 0.5) / middimen) * 0.25;
      y += ((util.random() - 0.5) / middimen) * 0.25;

      let ci = i % middimen;
      ci += ~~((Math.sqrt(Math.PI) * i) / middimen);

      x = Math.floor(x * dimen) / dimen;
      y = Math.floor(y * dimen) / dimen;
      ps[pi + PCLR] = ci % 4;
      ps[pi + PX] = x;
      ps[pi + PY] = y;
      ps[pi + PR] = ps[pi + PR2] = midr / 2;
      ps[pi + PGEN] = this.maxgen++;
    }
    //}
    return;
    function point(x: number, y: number, ix: number, iy: number): number {
      const pi = ps.length;

      for (let i = 0; i < PTOT; i++) {
        ps.push(0);
      }

      ps[pi] = x / repeat + ix;
      ps[pi + 1] = y / repeat + iy;

      return pi;
    }

    for (let i = 0; i < repeat * repeat; i++) {
      let x = i % repeat;
      let y = ~~(i / repeat);

      x /= repeat;
      y /= repeat;

      const a = 0.0;

      point(0.25 + a * 2, 0.25 + a * 2, x, y);
      point(0.25 + a * 2, 0.75 - a, x, y);
      point(0.75 - a, 0.75 - a, x, y);
      point(0.75 - a, 0.25 + a * 2, x, y);

      //point(0.5, 0.5, x, y);

      //point(1.0/3.0, 0.5, x, y);
      //point(0.77, 0.5, x, y);
      //point(0.5, 1.0/3.0, x, y);
      //point(0.5, 0.77, x, y);

      point(0.5, 0, x, y);
      point(0.0, 0.5, x, y);
    }
  }

  reset(dimen: number, appstate: AppState, mask_image: ImageData) {
    super.reset(dimen, appstate, mask_image);

    this.colortick = 0;
    this.dimen = dimen;
    this.hscale = this.hlvl = this.gen = this.maxgen = this.totfilled = 0;

    this.config.update();

    const cf = this.config;
    const gfuncs = grid_funcs.cartessian; //cf.VOID_HEX_MODE ? grid_funcs.hex : grid_funcs.cartessian;

    this.xy2grid = gfuncs.xy2grid;
    this.grid2xy = gfuncs.grid2xy;

    if (cf.MAKE_JOINT) {
      this.gridsize = ~~dimen;
      this.maxpoints = dimen * dimen * 4.0;
    } else {
      this.gridsize = ~~(dimen / 2);
      this.maxpoints = dimen * dimen;
    }

    this.grids = new Array(4);

    for (let i = 0; i < 4; i++) {
      const grid = new Float64Array(this.gridsize * this.gridsize * GTOT);
      grid.fill(-1, 0, grid.length);
      this.grids[i] = grid;

      for (let gi = 0; gi < grid.length; gi += GTOT) {
        grid[gi + GW] = grid[gi + GSUM] = 0;
      }
    }

    const hscale = (this.hscale = VC_HIEARCHIAL_SCALE);
    const r = 1.0 / this.gridsize;
    //var midr = r*0.5 + this.hscale*r*0.5;
    //var midr = r + (r*hscale - r)*cf.VOIDCLUSTER_MID_R;
    let midr = r * hscale;

    if (TEST_CLUSTER) {
      midr *= 0.4;
    }

    this.draw_rmul = r / midr;

    this.r = this.start_r = midr;
    this.min_r = r;
    this.max_r = r * this.hscale;

    this.points = [];

    const midsize = ~~(1.0 / midr + 0.5);

    this.midsize = midsize;
    this.dimen = dimen;

    const gridsize = this.gridsize;
    const msize = mask_image.width;
    const ps = this.points;

    if (!this.ignore_initial_points) {
      if (1 || VOID_BAYER_MODE) {
        this.gen_initial_structured(dimen, appstate, mask_image);
      } else {
        this.gen_initial_blue(this.config, dimen, appstate, mask_image);
      }
    }

    this.search_r = this.filter_r(this.config);
    const joint = this.config.MAKE_JOINT;

    for (let i = 0; i < ps.length; i += PTOT) {
      const x = ps[i];
      const y = ps[i + 1];
      const color = ps[i + PCLR];
      const ixy = this.xy2grid(x, y, gridsize);
      const ix = ~~(ixy[0] + 0.0);
      const iy = ~~(ixy[1] + 0.0);
      const idx = (iy * gridsize + ix) * GTOT;

      this.grids[color][idx] = i;

      const xy = this.grid2xy(ix + 0.0001, iy + 0.0001, gridsize);

      xy[0] = Math.fract(xy[0]);
      xy[1] = Math.fract(xy[1]);

      const ox = (color % 2) / 2;
      const oy = ~~(color / 2) / 2;

      //ps[pi] = x/2+ox, ps[pi+1] = y/2+oy;

      ps[i + PDX] = xy[0];
      ps[i + PDY] = xy[1];

      if (!joint) {
        ps[i] = xy[0] / 2 + ox;
        ps[i + 1] = xy[1] / 2 + oy;

        ps[i + PIX] = ~~((ix * gridsize + 0.0001) * msize + 0.0001);
        ps[i + PIY] = ~~((iy * gridsize + 0.0001) * msize + 0.0001);
      } else {
        ps[i + PIX] = ~~(xy[0] * dimen);
        ps[i + PIY] = ~~(xy[1] * dimen);
      }

      ps[i + PGEN] = 0;

      this.grid_sum_point_all(cf, i, 1);
      //this.grid_sum_point(this.grids[color], this.config, i, 1);
    }

    const size = this.gridsize;

    this.cells = [[], [], [], []];

    for (let j = 0; j < 4; j++) {
      for (let i = 0; i < size * size; i++) {
        if (this.grids[j][i * GTOT] < 0) {
          this.cells[j].push(i);
        }
      }
    }

    this.midpoints = ps.length / PTOT;
    this.regen_spatial();

    if (TEST_CLUSTER) {
      this.totfilled = this.midpoints;
      this.maxgen = this.midpoints;
      this.gen = 0;
    }

    this.raster();
  }

  grid_sum_point(grid: Float64Array, cf: MaskConfig, pi: number, sign: number, fac: number = 1.0) {
    const size = this.gridsize;
    const ps = this.points;
    const r = this.search_r; //this.filter_r();
    const colortick = ps[pi + PCLR];

    //we abuse pdx/pdy to store within-grid coordinates,
    //actual x/y store final position in cmyk tiling

    const x = ps[pi + PDX];
    const y = ps[pi + PDY];
    const ixy = this.xy2grid(x, y, size);
    const ix = ~~ixy[0];
    const iy = ~~ixy[1];
    let rd = ~~(r * size * Math.sqrt(2.0) + 2);
    rd = Math.max(rd, 1);
    const off = cconst.get_searchoff(rd);

    for (let i = 0; i < off.length; i++) {
      let ix2 = off[i][0] + ix;
      let iy2 = off[i][1] + iy;
      let x2 = x;
      let y2 = y;

      if (ix2 < 0) {
        ix2 += size;
        x2 += 1.0;
      } else if (ix2 >= size) {
        ix2 -= size;
        x2 -= 1.0;
      }

      if (iy2 < 0) {
        iy2 += size;
        y2 += 1.0;
      } else if (iy2 >= size) {
        iy2 -= size;
        y2 -= 1.0;
      }

      const xy3 = this.grid2xy(ix2 + 0.0001, iy2 + 0.0001, size);
      const x3 = xy3[0];
      const y3 = xy3[1];
      const dx = x3 - x2;
      const dy = y3 - y2;
      let dis = dx * dx + dy * dy;

      if (dis > r) continue;
      dis = dis != 0.0 ? Math.sqrt(dis) : 0.0;

      let w = 1.0 - dis / r;

      w = cf.VOIDCLUSTER_CURVE.evaluate(w) * fac;

      const idx = (iy2 * size + ix2) * GTOT;

      grid[idx + GW] += w * sign;
      grid[idx + GSUM] += sign;
    }
  }

  current_level() {
    return this.gen;
  }

  filter_r(cf: MaskConfig): number {
    const size = this.gridsize;
    const grid = this.grid;
    const ps = this.points;
    const maxpoints = this.gridsize * this.gridsize;
    const tfac = (maxpoints - this.points.length / PTOT) / maxpoints;
    let r =
      Math.sqrt(2.0) / Math.sqrt(this.gridsize * this.gridsize * 0.1 + this.points.length / PTOT);
    r *= cf.VC_FILTERWID;

    return r;
  }

  step(custom_steps?: number) {
    const cf = this.config;

    let steps = custom_steps ? custom_steps : STEPS;
    if (steps > 64) {
      steps = ~~(Math.log(steps) / Math.log(1.15)) + 40;
    }

    console.log("steps", steps);

    //steps = 1;

    //      let preparenext = false;

    for (let i = 0; !this.done() && i < steps; i++) {
      this.void_step();
    }

    /*
      if (preparenext) {
        let grid = this.grid;
        let gridsize = this.gridsize;
        
        this.totfilled = this.midpoints;
        
        for (let  i=this.midpoints*PTOT; i<this.points.length; i += PTOT) {
          this.points[i+PGEN] += this.midpoints;
        }
        
        this.maxgen += this.midpoints;
        
        for (let  i=0; i<grid.length; i += GTOT) {
          grid[i+GW] = 0;
          grid[i+GSUM] = 0;
        }
        
        for (let  i=0; i<this.midpoints; i++) {
          this.grid_sum_point(cf, i*PTOT, 1);
        }
      }//*/

    report("points:", this.points.length / PTOT);

    this.raster();
  }

  done() {
    return this.points.length / PTOT >= this.maxpoints;
  }

  void_step(custom_steps?: number) {
    const cf = this.config;
    const steps = custom_steps ? custom_steps : STEPS;
    const size = this.gridsize;
    const grid = this.grids[this.colortick];
    const ps = this.points;
    const rd = ~~(this.r * Math.sqrt(2) * size + 2.0);
    let maxf = -1e17;
    let minf = 1e17;
    let maxi = -1;
    let mini = -1;
    let minri = -1;
    let maxri = -1;
    //eek! slow!
    //stochastic to the rescue!

    const cells = this.cells[this.colortick];
    const tot = this.gridsize * 3;
    let cs = [];
    let idxs = [];
    for (let _i = 0; _i < tot; _i++) {
      const ri = ~~(Math.random() * cells.length * 0.9999999);
      idxs.push(cells[ri]);
    }

    //* . . .or not.
    idxs = [];
    for (let i = 0; i < cells.length; i++) {
      idxs.push(i);
    }
    //*/

    const empty = undefined;

    for (let _i = 0; _i < idxs.length; _i++) {
      const ri = idxs[_i];
      const i = cells[ri];

      if (cells.length == 0) break;
      if (grid[i * GTOT] >= 0) {
        cs.push(empty);
        continue;
      }

      const ix = i % size;
      const iy = ~~(i / size);
      //var f = this.filter(ix, iy);

      const f = grid[i * GTOT + GW];

      cs.push(f);

      if (f > maxf) {
        maxf = f;
        maxi = i;
        maxri = ri;
      }

      if (f < minf) {
        // || (Math.abs(f-minf)<Math.min(f, minf)*0.01 && Math.random() > 0.5)) {
        minf = f;
        mini = i;
        minri = ri;
      }
    }

    const idxs3 = [];
    const cs2 = [];
    let minidx: number | undefined = undefined;
    let minval: number | undefined = undefined;
    const joint = cf.MAKE_JOINT;

    for (let i = 0; i < idxs.length; i++) {
      if (cs[i] != empty) {
        if (minval === undefined || cs[i]! < minval) {
          minidx = i;
          minval = cs[i];
        }

        idxs3.push(idxs[i]);
        cs2.push(cs[i]);
      }
    }

    const dimen = this.dimen;
    const small_mask_mode = this.config.SMALL_MASK;

    if (cs.length == 0 || minval === undefined) {
      this.colortick = (this.colortick + 1) % 4;
      return;
    }

    idxs = idxs3;
    cs = cs2;

    //console.log("start");
    const last = undefined;
    let mini2 = 0;
    minf = 1e17;
    const seed = Math.random();
    const arr = [0, 0, 0, 0, 0, 0];

    mini2 = 0;

    minri = minidx!;
    mini = cells[minri];

    if (mini < 0) {
      this.colortick = (this.colortick + 1) % 4;
      return;
    }

    const ix = mini % size;
    const iy = ~~(mini / size);

    if (cells.length > 0) {
      cells[minri] = cells[cells.length - 1];
      cells.length--;
    }

    const xy = this.grid2xy(ix + 0.0001, iy + 0.0001, size);
    //xy[0] = Math.fract(xy[0]);
    //xy[1] = Math.fract(xy[1]);

    //are we generating stippling (i.e. not aligned to grid) mask?
    if (!this.config.SMALL_MASK) {
      //xy = this.optimize_grid_point(xy, ix, iy, pi);
    }

    const x2 = xy[0];
    const y2 = xy[1];
    const pi = ps.length;

    for (let i = 0; i < PTOT; i++) {
      ps.push(0);
    }

    const gridscale = this.gridsize / this.dimen;
    const gdimen = Math.floor(1.0 / gridscale + 0.5);

    //abuse dx/dy to store within-grid coordinates
    ps[pi + PDX] = x2;
    ps[pi + PDY] = y2;

    if (!joint) {
      let ox = ~~(this.colortick % gdimen);
      let oy = ~~(this.colortick / gdimen);
      ox *= gridscale;
      oy *= gridscale;

      ps[pi] = x2 * gridscale + ox;
      ps[pi + 1] = y2 * gridscale + oy;
      //ps[pi+PIX]  = ~~((xy[0]+0.0001)*this.mask_img.width*0.999999+0.0001);
      //ps[pi+PIY]  = ~~((xy[1]+0.0001)*this.mask_img.width*0.999999+0.0001);
    } else {
      const clr = this.colortick;
      const ox = clr % 2;
      const oy = ~~(clr / 2);

      ps[pi] = x2 + 0.0001 / dimen; // + ox/dimen/4;
      ps[pi + 1] = y2 + 0.0001 / dimen; // + oy/dimen/4;

      if (!small_mask_mode) {
        ps[pi] += ox / 2.0 / dimen;
        ps[pi + 1] += oy / 2.0 / dimen;
      }
    }

    ps[pi + PIX] = ~~(ps[pi] * dimen + 0.00001);
    ps[pi + PIY] = ~~(ps[pi] * dimen + 0.00001);
    ps[pi + PGEN] = this.maxgen;
    ps[pi + PCLR] = this.colortick; //(pi/PTOT) % 4;

    //this.maxgen = ps.length / PTOT;
    this.maxgen++;
    this.gen++;

    //ps[pi+PCLR] = this.cur_cmyk;
    //this.cur_cmyk = (this.cur_cmyk+1) % 4;

    this.kdtree.insert(ps[pi], ps[pi + 1], pi);

    grid[mini * GTOT + GIDX] = pi;

    this.update_fscale(true);
    this.grid_sum_point_all(cf, pi, 1);

    this.colortick = (this.colortick + 1) % 4;
  }

  raster() {
    const ps = this.points;
    const dimen = this.dimen;
    const mask = this.mask;
    const maskdimen = ~~this.mask_img.width;
    const joint = this.config.MAKE_JOINT;
    const small_mask_mode = this.config.SMALL_MASK;

    for (let i = 0; i < mask.length; i++) {
      mask[i] = 0.0;
    }

    for (let pi = 0; pi < ps.length; pi += PTOT) {
      const x = ps[pi];
      const y = ps[pi + 1];
      let ix = ~~ps[pi + PIX];
      let iy = ~~ps[pi + PIY];
      const clr = ps[pi + PCLR];
      let gen = ps[pi + PGEN] / this.maxgen;

      if (clr != 1) {
        //continue;
      }

      ix = ~~(x * maskdimen);
      iy = ~~(y * maskdimen);

      gen *= 255;
      const idx = (iy * maskdimen + ix) * 4;

      if (!joint) {
        mask[idx] = mask[idx + 1] = mask[idx + 2] = gen;
        mask[idx + 3] = 255;
      } else {
        mask[idx + clr] = gen;

        if (clr == 3 && !small_mask_mode) {
          mask[idx] = mask[idx + 1] = mask[idx + 2] = gen;
          mask[idx + 3] = 255;
        }
      }
    }
  }

  grid_sum_point_all(cf: MaskConfig, pi: number, sign: number) {
    const color = this.points[pi + PCLR];

    for (let j = 0; j < 4; j++) {
      const fac = color === j ? 1.0 : 0.5;
      this.grid_sum_point(this.grids[j], cf, pi, 1, fac);
    }
  }

  draw(g: CanvasRenderingContext2D) {
    const size = this.dimen;

    //return;

    g.strokeStyle = "grey";

    g.beginPath();
    const dx = 1.0 / size;
    for (let i = 0.0; i < size + 0.0; i++) {
      const i2 = i + 0.5;
      g.moveTo(i2 * dx, 0);
      g.lineTo(i2 * dx, 1);

      g.moveTo(0, i2 * dx);
      g.lineTo(1, i2 * dx);
    }
    g.stroke();
  }
};

//reuses normal void cluster's settings
sinterface.MaskGenerator.register(config, VoidClusterGeneratorCMYK, "VOID_CLUSTER_CMYK", 8);

export { Modes, config, VoidClusterGeneratorCMYK };
